import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import useSWR from 'swr';

import { apiClient } from '@src/clients';
import { ConstantErrorMetadataType } from '@src/clients/clerk/api';
import { IdentityContext } from '@src/contexts/identity/context';
import {
  announcementMap,
  setAnnouncement
} from '@src/redux/_reducers/appReducer';
import { getUSTimeFormat } from '@src/utils';

export const usePendingOrderBanner = () => {
  const { activeOrgInfo, activeOrg } = useContext(IdentityContext);

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const isKycSuccessful = activeOrgInfo?.kyc_successful === true;
  // check for pending orders
  const { data: { data: { order_items: order_items_all = [] } = {} } = {} } =
    useSWR(isKycSuccessful ? '/check-pending-orders' : null, () =>
      apiClient.clerkApi.v1OrderItemsList({
        error_metadata_type:
          ConstantErrorMetadataType.ErrorMetadataTypeTokensInsufficient,
        limit: 1
      })
    );

  const orderErrorMetadata = useMemo(() => {
    const { error_metadata = {}, id = '' } = order_items_all?.[0] || {};
    const { template_variables = {} } = error_metadata;
    const { final_date = '', task_id = '' } = template_variables;

    return {
      final_date,
      task_id,
      id
    };
  }, [order_items_all]);

  const redirectToOrdersFromBanner = useCallback(() => {
    const { task_id, id } = orderErrorMetadata;
    navigate(
      `/${activeOrg?.name}/order-desk/order-status/${id}?task_id=${task_id}`
    );
  }, [orderErrorMetadata, navigate, activeOrg?.name]);

  //   update announcement state
  useEffect(() => {
    if (order_items_all.length > 0) {
      dispatch(
        setAnnouncement({
          id: 'pending-order-banner',
          type: announcementMap.error,
          message: `Order is on hold due to insufficient tokens. Clear your payment by ${getUSTimeFormat(orderErrorMetadata.final_date) || ''}`,
          action: redirectToOrdersFromBanner,
          actionLabel: 'Continue',
          leadingIcon: <ExclamationTriangleIcon />,
          priority: 2
        })
      );
    }
  }, [
    dispatch,
    orderErrorMetadata.final_date,
    order_items_all,
    redirectToOrdersFromBanner
  ]);

  return null;
};
